import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { clsx } from '../../../ds/clsx';
import { Avatar, ImageSlot, Pill, TextLink } from '../../../ds/components';
import type { DealRecord, Participant } from '../types';
import styles from './record.module.css';

/** Sidebar cards and small pieces shared by the tabs of the deal record (Deal-Akte A2). */

export function SideCard({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <section className={clsx(styles.card, styles.cardSide, className)} aria-label={label}>
      <div className={styles.eyebrow}>{label}</div>
      {children}
    </section>
  );
}

/** „Nächster Schritt" of the sidebar: title, due pill, responsible. */
export function NextStepCard({ deal }: { deal: DealRecord }) {
  const step = deal.nextStep;
  return (
    <SideCard label="Nächster Schritt">
      {step ? (
        <>
          <div className={styles.nextStepTitle}>{step.title}</div>
          <div className={styles.nextStepMeta}>
            <Pill variant="accent">{step.dueLabel}</Pill>
            {step.responsibleShort ? <span>{step.responsibleShort}</span> : null}
          </div>
        </>
      ) : (
        <div className={styles.nextStepTitle} style={{ color: 'var(--text-muted)' }}>
          Kein nächster Schritt
        </div>
      )}
    </SideCard>
  );
}

function ParticipantRow({ participant, tone }: { participant: Participant | null; tone: 'navy' | 'sand' }) {
  if (!participant) return null;
  return (
    <div className={styles.participant}>
      <Avatar initials={participant.user.initials} name={participant.user.name} size={26} tone={tone} />
      <span className={styles.participantText}>
        <span className={styles.participantName}>{participant.user.shortName}</span>
        <span className={styles.participantRole}>
          {participant.roleLabel}
          {participant.detail ? ` · ${participant.detail}` : ''}
        </span>
      </span>
    </div>
  );
}

/** „Beteiligte": closer · setter · financier · team lead. */
export function ParticipantsCard({ deal }: { deal: DealRecord }) {
  const { closer, setter, financier, teamLead } = deal.participants;
  return (
    <SideCard label="Beteiligte">
      <div className={styles.participants}>
        <ParticipantRow participant={closer} tone="navy" />
        <ParticipantRow participant={setter} tone="sand" />
        {financier ? (
          <ParticipantRow participant={financier} tone="navy" />
        ) : (
          <div className={styles.participant}>
            <Avatar initials="–" name="Kein Finanzierer" size={26} tone="quiet" />
            <span className={styles.participantText}>
              <span className={styles.participantName}>Kein Finanzierer</span>
              <span className={styles.participantRole}>noch nicht zugewiesen</span>
            </span>
          </div>
        )}
        <ParticipantRow participant={teamLead} tone="sand" />
      </div>
    </SideCard>
  );
}

/** The property card of the sidebar with the image slot and „Zur Objektakte". */
export function PropertyCard({ deal }: { deal: DealRecord }) {
  const navigate = useNavigate();
  if (!deal.property) return null;
  const property = deal.property;
  return (
    <section className={clsx(styles.card, styles.cardSide)} aria-label="Objekt">
      <ImageSlot
        alt={property.name}
        placeholder={`Objektbild · ${property.name}`}
        height={120}
        chrome="frame"
        className={styles.propertyImage}
      />
      <div className={styles.propertyName}>{property.name}</div>
      <div className={styles.propertyLine}>{property.line}</div>
      <div className={styles.propertyLink}>
        <TextLink as="button" size="small" onClick={() => void navigate(property.href)}>
          Zur Objektakte
        </TextLink>
      </div>
    </section>
  );
}
